import React, { useState } from 'react';
import { RankingSnapshot } from '../services/api';
import {
  generateSimulationPoints,
  getStatusColorClass,
  getStatusLabel
} from '../utils/penaltySimulator';
import {
  calculateActiveHoursSinceSale,
  calculateExpectedHours,
  formatTimeFromHours
} from '../utils/penaltyCalculations';
import { getActiveHoursLabel, ACTIVE_HOURS_CONFIG } from '../config/activeHours';

interface PenaltySimulatorProps {
  ranking: RankingSnapshot;
}

const PenaltySimulator: React.FC<PenaltySimulatorProps> = ({ ranking }) => {
  const [showTechnicalNote, setShowTechnicalNote] = useState(false);

  // Extract data
  const dpd7 = ranking.dpd_7 || 0.01;
  const baseScore = ranking.base_score || 0;
  const expectedHours = calculateExpectedHours(dpd7);
  const currentPenaltyFactor = ranking.penalty_v2_factor || 1.0;
  const currentDiscountPct = (1 - currentPenaltyFactor) * 100;

  // IMPORTANT: active_hours_overdue é o ATRASO relativo à expectativa, não o tempo total
  const delayHours = ranking.active_hours_overdue || 0;

  // Calcular tempo REAL desde última venda (horas ativas)
  // CRITICAL: Sempre usar calculation_timestamp do snapshot, NUNCA hora atual do browser!
  const actualHoursSinceSale =
    ranking.last_sale_timestamp && ranking.calculation_timestamp
      ? calculateActiveHoursSinceSale(
          ranking.last_sale_timestamp,
          ranking.calculation_timestamp
        )
      : 0;

  // Penalty breakdown
  const penaltyApplied = ranking.penalty_applied || 1.0;
  const priceImpactFactor = (currentPenaltyFactor > 0) ? (penaltyApplied / currentPenaltyFactor) : 1.0;
  const priceImpactPct = (priceImpactFactor - 1) * 100; // Positivo = boost, Negativo = penalty

  // Generate simulation points (penalty applies only to revenue component)
  const weightedRevenue = ranking.weighted_revenue || 0;
  const simulationPoints = generateSimulationPoints(
    baseScore,
    dpd7,
    delayHours,
    currentPenaltyFactor,
    penaltyApplied,
    weightedRevenue
  );

  // Calcular o máximo da escala: 2× expectativa (relativo a cada produto)
  const maxScaleHours = expectedHours * 2;

  // Calculate progress percentage for visual bar (baseado no tempo REAL, não atraso)
  const progressPct = Math.min((actualHoursSinceSale / maxScaleHours) * 100, 100);
  const expectedPct = (expectedHours / maxScaleHours) * 100;

  // Format times
  const expectedTimeFormatted = formatTimeFromHours(expectedHours);
  const actualTimeFormatted = formatTimeFromHours(actualHoursSinceSale);
  const delayTimeFormatted = formatTimeFromHours(delayHours);
  const maxScaleFormatted = formatTimeFromHours(maxScaleHours);

  return (
    <div className="penalty-simulator">
      {/* Header Explanation */}
      <div className="alert alert-info mb-4">
        <h6 className="alert-heading">
          <i className="bi bi-info-circle me-2"></i>
          Sistema de Penalização Temporal V2.0
        </h6>
        <p className="mb-2">
          O sistema penaliza produtos que ficam sem vender por períodos prolongados,
          considerando apenas o <strong>horário comercial ativo ({getActiveHoursLabel()})</strong>.
        </p>
        <p className="mb-0">
          A penalização é <strong>RELATIVA</strong> à velocidade esperada de cada produto:
        </p>
        <ul className="mb-0 mt-2">
          <li>Produtos mais <strong>rápidos</strong> (DPD alto) têm expectativa de venda menor</li>
          <li>Produtos mais <strong>lentos</strong> (DPD baixo) têm mais tempo antes de serem penalizados</li>
        </ul>
      </div>

      {/* Product Characteristics Panel */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h6 className="mb-0">
            <i className="bi bi-speedometer2 me-2"></i>
            Características do Seu Produto
          </h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <span className="text-muted">DPD 7 dias:</span>
                <strong className="ms-2">{dpd7.toFixed(2)} unidades/dia</strong>
              </div>
              <div className="mb-3">
                <span className="text-muted">Expectativa de venda:</span>
                <strong className="ms-2">1 unidade a cada {expectedTimeFormatted}</strong>
              </div>
              <div className="mb-3">
                <span className="text-muted">Horário ativo:</span>
                <strong className="ms-2">{getActiveHoursLabel()} ({ACTIVE_HOURS_CONFIG.TOTAL_HOURS}h/dia)</strong>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <span className="text-muted">Tempo desde última venda (horário ativo):</span>
                {ranking.calculation_timestamp ? (
                  <strong className="ms-2">{actualTimeFormatted}</strong>
                ) : (
                  <span className="ms-2 text-warning">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    Timestamp não disponível
                  </span>
                )}
              </div>
              <div className="mb-3">
                <span className="text-muted">Atraso em relação à expectativa:</span>
                <strong className="ms-2">{delayTimeFormatted}</strong>
                {ranking.calculation_timestamp && actualHoursSinceSale > 0 && (
                  <span className={`badge ms-2 bg-${
                    actualHoursSinceSale < expectedHours ? 'success' :
                    actualHoursSinceSale < expectedHours * 1.5 ? 'warning' : 'danger'
                  }`}>
                    {((actualHoursSinceSale / expectedHours) * 100).toFixed(0)}%
                  </span>
                )}
              </div>
              <div className="mb-0">
                <span className="text-muted">Penalização aplicada:</span>
                <strong className={`ms-2 text-${currentDiscountPct > 20 ? 'danger' : currentDiscountPct > 10 ? 'warning' : 'success'}`}>
                  {currentDiscountPct.toFixed(1)}%
                </strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Penalty Breakdown Panel */}
      <div className="card mb-4">
        <div className="card-header bg-warning text-dark">
          <h6 className="mb-0">
            <i className="bi bi-pie-chart me-2"></i>
            Decomposição de Penalizações
          </h6>
        </div>
        <div className="card-body">
          <div className="alert alert-light mb-3">
            <small className="text-muted">
              O score final é calculado aplicando <strong>todas</strong> as penalizações ao score base.
            </small>
          </div>

          <div className="row">
            {/* V2 Temporal Penalty */}
            <div className="col-md-6">
              <div className="card border-primary">
                <div className="card-body text-center">
                  <div className="small text-muted mb-1">Penalização V2 (Temporal)</div>
                  <div className="h4 mb-1">
                    <span className={`badge ${currentDiscountPct > 20 ? 'bg-danger' : currentDiscountPct > 10 ? 'bg-warning' : 'bg-success'}`}>
                      {currentDiscountPct.toFixed(1)}%
                    </span>
                  </div>
                  <div className="small text-muted">
                    Fator: {currentPenaltyFactor.toFixed(3)}
                  </div>
                </div>
              </div>
            </div>

            {/* Price Impact */}
            <div className="col-md-6">
              <div className={`card ${priceImpactFactor > 1 ? 'border-success' : priceImpactFactor < 1 ? 'border-warning' : 'border-secondary'}`}>
                <div className="card-body text-center">
                  <div className="small text-muted mb-1">Price Impact</div>
                  <div className="h4 mb-1">
                    <span className={`badge ${priceImpactPct > 0 ? 'bg-success' : priceImpactPct < 0 ? 'bg-danger' : 'bg-secondary'}`}>
                      {priceImpactPct > 0 ? '+' : ''}{priceImpactPct.toFixed(1)}%
                    </span>
                  </div>
                  <div className="small text-muted mb-1">
                    Fator: {priceImpactFactor.toFixed(3)}
                  </div>
                  {/* Price information */}
                  {ranking.preco_atual && (
                    <div className="mt-2 pt-2 border-top">
                      <div className="small">
                        <span className="text-muted">Preço atual:</span>{' '}
                        <strong className="text-dark">€{ranking.preco_atual.toFixed(2)}</strong>
                      </div>
                      {ranking.preco_anterior && ranking.preco_anterior !== ranking.preco_atual && (
                        <div className="small mt-1">
                          <span className="text-muted">Preço anterior:</span>{' '}
                          <span className="text-secondary">€{ranking.preco_anterior.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Formula visualization */}
          <div className="mt-3 text-center">
            <small className="text-muted">
              <code className="bg-light p-2 rounded">
                Score Final = Base ({baseScore.toFixed(4)}) × V2 ({currentPenaltyFactor.toFixed(3)}) × Price ({priceImpactFactor.toFixed(3)}) = {ranking.final_score?.toFixed(4)}
              </code>
            </small>
          </div>
        </div>
      </div>

      {/* Visual Progress Bar */}
      <div className="card mb-4">
        <div className="card-body">
          <h6 className="mb-3">Progresso até Próxima Venda Esperada</h6>
          <div className="position-relative" style={{ height: '80px' }}>
            {/* Progress bar */}
            <div className="progress position-relative" style={{ height: '30px' }}>
              <div
                className={`progress-bar ${
                  actualHoursSinceSale < expectedHours ? 'bg-success' :
                  actualHoursSinceSale < expectedHours * 1.5 ? 'bg-warning' : 'bg-danger'
                }`}
                role="progressbar"
                style={{ width: `${Math.min(progressPct, 100)}%` }}
                aria-valuenow={progressPct}
                aria-valuemin={0}
                aria-valuemax={100}
              >
              </div>

              {/* Marcador da expectativa */}
              <div
                className="position-absolute top-0 bottom-0"
                style={{
                  left: `${expectedPct}%`,
                  width: '2px',
                  backgroundColor: '#0d6efd',
                  zIndex: 10
                }}
                title={`Expectativa: ${expectedTimeFormatted}`}
              >
                <div
                  className="position-absolute bg-primary text-white px-1 rounded"
                  style={{
                    top: '-20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: '0.65rem',
                    whiteSpace: 'nowrap'
                  }}
                >
                  ▼ {expectedTimeFormatted}
                </div>
              </div>
            </div>

            {/* Labels */}
            <div className="d-flex justify-content-between mt-3 small text-muted">
              <span>0h (início)</span>
              <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                ← Escala até 2× expectativa →
              </span>
              <span>{maxScaleFormatted}</span>
            </div>
            <div className="text-center mt-1">
              {ranking.calculation_timestamp ? (
                <span className="text-dark">
                  Tempo desde última venda: <strong>{actualTimeFormatted}</strong> | Expectativa: <strong>{expectedTimeFormatted}</strong> | Atraso: <strong>{delayTimeFormatted}</strong>
                </span>
              ) : (
                <span className="badge bg-warning">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Cálculo de tempo não disponível (falta calculation_timestamp)
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Simulation Table */}
      <div className="card mb-4">
        <div className="card-header">
          <h6 className="mb-0">
            <i className="bi bi-table me-2"></i>
            Progressão de Penalização para Este Produto
          </h6>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '15%' }}>Tempo sem Vender</th>
                  <th style={{ width: '15%' }}>vs Expectativa</th>
                  <th style={{ width: '15%' }}>Penalização</th>
                  <th style={{ width: '15%' }}>Desconto</th>
                  <th style={{ width: '15%' }}>Score Final</th>
                  <th style={{ width: '25%' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {simulationPoints.map((point, index) => {
                  const isCurrent = Math.abs(point.hours - delayHours) < 0.1;
                  const isExpectation = point.label.includes(expectedTimeFormatted);
                  const rowClass = isCurrent
                    ? 'table-primary fw-bold'
                    : isExpectation
                    ? 'table-warning'
                    : '';

                  return (
                    <tr key={index} className={rowClass}>
                      <td>
                        {point.label}
                        {isCurrent && (
                          <span className="badge bg-primary ms-2">ATUAL</span>
                        )}
                      </td>
                      <td>
                        {point.vsExpectationPct.toFixed(0)}%
                      </td>
                      <td>
                        {(point.penaltyFactor * 100).toFixed(0)}%
                      </td>
                      <td>
                        <span className={`text-${point.discountPct > 20 ? 'danger' : point.discountPct > 10 ? 'warning' : 'success'}`}>
                          {point.discountPct > 0 ? '-' : ''}{point.discountPct.toFixed(1)}%
                        </span>
                      </td>
                      <td>
                        {point.finalScore.toFixed(4)}
                      </td>
                      <td>
                        <span className={`badge bg-${getStatusColorClass(point.status)}`}>
                          {point.icon} {getStatusLabel(point.status)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Technical Note (Collapsible) */}
      <div className="card">
        <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setShowTechnicalNote(!showTechnicalNote)}>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="bi bi-gear me-2"></i>
              Como funciona? (Nota Técnica)
            </h6>
            <i className={`bi bi-chevron-${showTechnicalNote ? 'up' : 'down'}`}></i>
          </div>
        </div>
        {showTechnicalNote && (
          <div className="card-body">
            <h6>Configuração do Sistema</h6>
            <ul>
              <li><strong>Decay Rate:</strong> 0.05 (taxa de decaimento exponencial)</li>
              <li><strong>Score Mínimo:</strong> 10% (piso de penalização)</li>
              <li><strong>Horário Ativo:</strong> Apenas {getActiveHoursLabel()} são contabilizadas ({ACTIVE_HOURS_CONFIG.TOTAL_HOURS}h/dia)</li>
            </ul>

            <h6 className="mt-3">Fórmula Matemática</h6>
            <div className="alert alert-secondary">
              <code>penalty = 0.10 + 0.90 × exp(-0.05 × horas_ativas)</code>
            </div>

            <h6>Como é Calculada a Expectativa?</h6>
            <p>
              A expectativa de venda é calculada dividindo as <strong>{ACTIVE_HOURS_CONFIG.TOTAL_HOURS} horas ativas</strong> por dia
              pelo <strong>DPD 7</strong> (média de vendas diárias):
            </p>
            <div className="alert alert-secondary">
              <code>expectativa_horas = {ACTIVE_HOURS_CONFIG.TOTAL_HOURS} / DPD_7</code>
              <br />
              <code>Para este produto: {ACTIVE_HOURS_CONFIG.TOTAL_HOURS} / {dpd7.toFixed(2)} = {expectedHours.toFixed(2)} horas</code>
            </div>

            <h6>Por que Modo Simplificado?</h6>
            <p className="mb-2">
              O sistema atual usa <strong>modo simplificado (time_based)</strong>, onde todos os produtos
              usam a mesma taxa de decaimento. Existe também um <strong>modo por velocidade</strong> que
              aplica penalizações diferentes baseadas no DPD do produto (ultra-fast, fast, medium, slow).
            </p>

            <h6 className="mt-3">Price Impact Penalty</h6>
            <p className="mb-0">
              Além da penalização temporal V2.0, o sistema também aplica uma <strong>penalização de impacto de preço</strong>.
              Esta penalização é calculada baseada em mudanças de preço e seu impacto previsto nas vendas (DPD).
              O score final é o resultado de: <code>base_score × penalty_v2 × price_impact</code>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PenaltySimulator;
